/**
 * Device Management Tab - Admin Panel
 * Manages tablet devices, their status, and staff assignments
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Tablet, 
  Monitor, 
  Coffee, 
  ChefHat, 
  GlassWater, 
  Users,
  Wifi,
  WifiOff,
  Battery,
  RefreshCw,
  Plus,
  Settings,
  Power,
  LogOut,
  User,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Station type icons and colors
const STATION_CONFIG = {
  pos: { icon: Monitor, label: 'POS Terminal', color: 'green' },
  counter: { icon: Users, label: 'Counter Display', color: 'orange' },
  barista: { icon: Coffee, label: 'Barista Station', color: 'blue' },
  kitchen: { icon: ChefHat, label: 'Kitchen Station', color: 'red' },
  juicer: { icon: GlassWater, label: 'Juice Bar', color: 'yellow' },
  server: { icon: Tablet, label: 'Server Tablet', color: 'purple' }
};

// Color to Tailwind class mapping
const COLOR_CLASSES = {
  green: { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-500', light: 'bg-green-100 dark:bg-green-900/30' },
  orange: { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-500', light: 'bg-orange-100 dark:bg-orange-900/30' },
  blue: { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-500', light: 'bg-blue-100 dark:bg-blue-900/30' },
  red: { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-500', light: 'bg-red-100 dark:bg-red-900/30' },
  yellow: { bg: 'bg-yellow-500', border: 'border-yellow-500', text: 'text-yellow-500', light: 'bg-yellow-100 dark:bg-yellow-900/30' },
  purple: { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-500', light: 'bg-purple-100 dark:bg-purple-900/30' }
};

export default function DeviceManagementTab() {
  const [devices, setDevices] = useState([]);
  const [statusSummary, setStatusSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await api.get('/devices');
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to load devices');
    }
  }, []);

  const fetchStatusSummary = useCallback(async () => {
    try {
      const response = await api.get('/devices/status/summary');
      setStatusSummary(response.data);
    } catch (error) {
      console.error('Error fetching status summary:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchDevices(), fetchStatusSummary()]);
    setLoading(false);
  }, [fetchDevices, fetchStatusSummary]);

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDevices();
      fetchStatusSummary();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadData, fetchDevices, fetchStatusSummary]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Devices refreshed');
  };

  const handleForceLogout = async (deviceId) => {
    try {
      await api.post(`/device-sessions/force-logout/${deviceId}`);
      toast.success('Device logged out');
      await fetchDevices();
    } catch (error) {
      console.error('Error forcing logout:', error);
      toast.error('Failed to logout device');
    }
  };

  const handleToggleActive = async (deviceId, currentActive) => {
    try {
      if (currentActive) {
        await api.delete(`/devices/${deviceId}`);
      } else {
        await api.post(`/devices/${deviceId}/reactivate`);
      }
      toast.success(currentActive ? 'Device deactivated' : 'Device reactivated');
      await fetchDevices();
    } catch (error) {
      console.error('Error toggling device:', error);
      toast.error('Failed to update device');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  // Group devices by station type
  const devicesByType = devices.reduce((acc, device) => {
    const type = device.station_type || 'unknown';
    if (!acc[type]) acc[type] = [];
    acc[type].push(device);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Device Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage tablets and station displays
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status Summary Cards */}
      {statusSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Tablet className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statusSummary.total_devices}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Devices</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Wifi className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{statusSummary.online}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Online</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <WifiOff className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{statusSummary.offline}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Offline</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{statusSummary.logged_in}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Staff Logged In</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devices Grid by Station Type */}
      <div className="space-y-6">
        {Object.entries(STATION_CONFIG).map(([type, config]) => {
          const typeDevices = devicesByType[type] || [];
          if (typeDevices.length === 0) return null;
          
          const Icon = config.icon;
          const colorClass = COLOR_CLASSES[config.color];
          
          return (
            <div key={type} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${colorClass.light}`}>
                  <Icon className={`w-4 h-4 ${colorClass.text}`} />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{config.label}</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">({typeDevices.length})</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {typeDevices.map((device) => (
                  <DeviceCard
                    key={device.device_id}
                    device={device}
                    colorClass={colorClass}
                    onForceLogout={handleForceLogout}
                    onToggleActive={handleToggleActive}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeviceCard({ device, colorClass, onForceLogout, onToggleActive }) {
  const isOnline = device.is_online;
  const hasStaff = !!device.current_staff_id;
  
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border-2 ${device.is_active ? colorClass.border : 'border-gray-300 dark:border-gray-600'} overflow-hidden transition-all hover:shadow-lg ${!device.is_active ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div className={`px-4 py-3 ${colorClass.light} border-b ${colorClass.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-gray-900 dark:text-white">{device.device_id}</span>
            {!device.is_active && (
              <span className="px-2 py-0.5 text-xs bg-gray-500 text-white rounded-full">Inactive</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isOnline ? (
              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Online
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                Offline
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Body */}
      <div className="p-4 space-y-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{device.device_name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{device.description}</p>
        </div>
        
        {/* Staff Info */}
        {hasStaff ? (
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <User className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              {device.current_staff_name}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">No staff logged in</span>
          </div>
        )}
        
        {/* Battery (if available) */}
        {device.battery_level !== null && device.battery_level !== undefined && (
          <div className="flex items-center gap-2">
            <Battery className={`w-4 h-4 ${device.battery_level < 20 ? 'text-red-500' : device.battery_level < 50 ? 'text-yellow-500' : 'text-green-500'}`} />
            <span className="text-sm text-gray-600 dark:text-gray-300">{device.battery_level}%</span>
          </div>
        )}
        
        {/* Last Seen */}
        {device.last_heartbeat && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            Last seen: {new Date(device.last_heartbeat).toLocaleTimeString()}
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
        {hasStaff && (
          <button
            onClick={() => onForceLogout(device.device_id)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <LogOut className="w-3 h-3" />
            Force Logout
          </button>
        )}
        <button
          onClick={() => onToggleActive(device.device_id, device.is_active)}
          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            device.is_active 
              ? 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600' 
              : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
        >
          <Power className="w-3 h-3" />
          {device.is_active ? 'Deactivate' : 'Reactivate'}
        </button>
      </div>
    </div>
  );
}
